import React, { createContext, useMemo } from 'react'

import { serverSideQuery, useQuery } from '@/api'
import { CUSTOM_PAGES_ENDPOINT, DEFAULT_CONTEXT_VALUES } from '@/constants'
import type {
    CustomPageContextType,
    CustomPageParams,
    CustomPageProviderProps,
    CustomPageSelectors,
    CustomPageState,
} from '@/context/custom-page/types'
import type { CmsCustomPage } from '@/types/app-types'

import { customPageSelector, breadcrumbSelector } from './selectors'

const CustomPageContext = createContext<CustomPageContextType>(DEFAULT_CONTEXT_VALUES)
const { Provider } = CustomPageContext

const getQueryDetails = (initialParams: CustomPageParams) => {
    const { id } = initialParams
    return {
        endpoint: `${CUSTOM_PAGES_ENDPOINT}/${id}`,
        params: {},
    }
}

export const CustomPageProvider: React.FC<CustomPageProviderProps> = ({ children, initialParams, initialProps }) => {
    const { endpoint, params } = useMemo(() => getQueryDetails(initialParams), [initialParams])

    const { fetching, data, error } = useQuery<CmsCustomPage>({ endpoint, params, initialData: initialProps?.data })

    const contextValue: CustomPageContextType = useMemo(() => {
        const state: CustomPageState = { fetching, data, error }

        const selectors: CustomPageSelectors = {
            customPage: customPageSelector(state),
            breadcrumb: breadcrumbSelector(state),
            id: initialParams.id,
        }

        return { state, selectors }
    }, [initialParams, fetching, data, error])

    return <Provider value={contextValue}>{children}</Provider>
}

export const serverSideRead = async (initialParams: CustomPageParams, maxAgeMs?: number): Promise<CmsCustomPage> => {
    const { endpoint, params } = getQueryDetails(initialParams)
    const queryInstance = serverSideQuery<CmsCustomPage>(endpoint)
    return await queryInstance(params, maxAgeMs)
}

export default CustomPageContext
